import {useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent} from 'react';
import type {EventContext, TweAdvisorProduct} from '../host/types';
import {EventBus, safeError} from '../host/eventBus';
import {priceLabel, safeUrl} from '../constructor/productAdapter';
import {resolveAgentContent, resolveContentTemplate} from '../content';
import {registerProductCardSizing} from './productCardSizing';

function productData(product: TweAdvisorProduct): Record<string, unknown> {
  return {
    product,
    productId: product.id,
    productName: product.name,
    ...(product.variationId && {variationId: product.variationId}),
  };
}

export function ProductCard({product, context, bus, displayKey, trackClick}: {product: TweAdvisorProduct; context: EventContext; bus: EventBus; displayKey: string; trackClick: () => void}) {
  const content = resolveAgentContent().products;
  const [basket, setBasket] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const card = useRef<HTMLElement>(null);
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const busy = useRef(false);

  useLayoutEffect(() => card.current ? registerProductCardSizing(card.current) : undefined, [product.name, product.secondaryName, product.size, product.strength]);

  useEffect(() => () => {
    alive.current = false;
    clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    const element = card.current;
    if (!element) return;
    const shown = () => bus.once(displayKey, () => bus.emit('onProductDisplayed', productData(product), context));
    if (!('IntersectionObserver' in window)) {
      shown();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        shown();
        observer.disconnect();
      }
    }, {threshold: .2});
    observer.observe(element);
    return () => observer.disconnect();
  }, [bus, displayKey, context, product]);

  const buildUrl = (ctx: EventContext) => {
    const value = bus.options.actions?.buildProductUrl?.(product, ctx) ?? `https://www.thewhiskyexchange.com/p/${encodeURIComponent(product.id)}`;
    const url = safeUrl(value);
    if (!url) throw new Error('Host buildProductUrl must return an http(s) URL.');
    return url;
  };

  const href = useMemo(() => {
    try {
      return buildUrl({...context, interaction: 'card'});
    } catch {
      return undefined;
    }
  }, [product.id, context.searchResultId]);

  const navigate = async (event: MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    const clickedElement = (event.target as HTMLElement).closest<HTMLElement>('[data-interaction]');
    const interaction = clickedElement?.dataset.interaction === 'image'
      ? 'image'
      : clickedElement?.dataset.interaction === 'title'
        ? 'title'
        : 'card';
    const ctx: EventContext = {...context, interaction};
    try {
      try {
        trackClick();
      } catch (error) {
        bus.error(error, ctx, 'CONSTRUCTOR_TRACKING_FAILED', 'product_interaction');
      }
      bus.emit('onProductClick', productData(product), ctx);
      const url = buildUrl(ctx);
      if (bus.options.actions?.navigateToProduct) await bus.options.actions.navigateToProduct(url, product, ctx);
      else window.location.assign(url);
    } catch (error) {
      bus.error(error, ctx, 'PRODUCT_INTERACTION_FAILED', 'product_interaction');
    }
  };

  const addToBasket = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy.current) return;
    busy.current = true;
    const quantity = 1;
    const ctx: EventContext = {...context, interaction: 'add-to-basket'};
    bus.emit('onAddToBasketClick', {...productData(product), quantity}, ctx);
    setBasket('loading');
    try {
      if (!bus.options.actions?.addToBasket) throw new Error('No host addToBasket action configured.');
      const result = await bus.options.actions.addToBasket(product, quantity, ctx);
      if (result === false || (typeof result === 'object' && result !== null && 'success' in result && result.success === false)) {
        throw new Error('Host basket rejected the request.');
      }
      bus.emit('onAddToBasketSuccess', {...productData(product), quantity, hostResult: result}, ctx);
      if (alive.current) {
        setBasket('success');
        timer.current = setTimeout(() => {
          if (alive.current) setBasket('idle');
          busy.current = false;
        }, 2000);
      }
    } catch (error) {
      const info = safeError(error, 'BASKET_FAILED');
      bus.emit('onAddToBasketError', {
        ...productData(product),
        quantity,
        error: {...info, recoverable: true},
        errorCode: info.code,
        errorMessage: info.message,
        recoverable: true,
      }, ctx);
      bus.error({...info, recoverable: true}, ctx, 'BASKET_FAILED', 'add_to_basket', true);
      if (alive.current) setBasket('error');
      busy.current = false;
    }
  };

  return <article ref={card} className="product-card twe-drinks-advisor__product-card" data-product-id={product.id} onClick={navigate}>
    <a className="twe-drinks-advisor__product-link" href={href} aria-label={resolveContentTemplate(content.viewProductAriaLabel, {ProductName: product.name})}>
      <div className="product-card__image-container" data-interaction="image">
        {product.imageUrl && <img className="product-card__image" src={safeUrl(product.imageUrl)} alt={product.name} loading="lazy"/>}
        {product.saving != null && product.saving > 0 && <div className="product-extras product-extras--grid">
          <div className="product-extras-flash product-extras-flash--purple">
            <p className="product-extras-flash__content">Save<br/>{priceLabel({...product, price: product.saving})}</p>
          </div>
        </div>}
      </div>
      <div className="product-card__content">
        <p className="product-card__name" data-interaction="title">{product.name}{product.secondaryName && <span className="product-card__name-secondary">{product.secondaryName}</span>}</p>
        <p className="product-card__meta">{[product.size, product.strength].filter(Boolean).join(' / ')}</p>
      </div>
      <div className="product-card__data"><p className="product-card__price">{priceLabel(product)}</p></div>
    </a>
    <div className="product-card__action">
      <div className="product-card__atb">
        <button type="button" className="product-card__button cta-button cta-button--regular" onClick={addToBasket} disabled={basket === 'loading' || basket === 'success' || product.inStock === false || !product.id} aria-label={resolveContentTemplate(basket === 'success' ? content.addedToBasketAriaLabel : content.addToBasketAriaLabel, {ProductName: product.name})}>
          {basket === 'loading' ? content.addingToBasket : basket === 'success' ? content.addedToBasket : content.addToBasket}
        </button>
      </div>
    </div>
    <div className="twe-drinks-advisor__basket-status" aria-live="polite">{basket === 'error' ? content.addToBasketFailed : product.inStock === false ? content.currentlyUnavailable : basket === 'success' ? content.addToBasket : ''}</div>
  </article>;
}
